import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Loader2, Save, Zap, Settings, BarChart3, FileUp, Pencil, MapPin } from 'lucide-react';
import { LocationImportModal } from './shared/LocationImportModal';
import { EditLocationsModal } from './shared/EditLocationsModal';
import { FloorPlanManager } from './shared/FloorPlanManager';
import { FieldCell } from './shared/FieldCell';

export const GeneralInfoSection = ({ projectId, canEditCSIItems, onDirtyChange }) => {
  const [form, setForm] = useState({
    utilityProvider: '', serviceVoltage: '', phase: '', serviceCapacity: '',
    mainSwitchboard: '', serviceDisconnectRating: '', estimatedLoad: '',
  });
  const [savedForm, setSavedForm] = useState(null);
  const [floorPlan, setFloorPlan] = useState(null);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [locations, setLocations] = useState([]);
  const [editLocationsOpen, setEditLocationsOpen] = useState(false);

  const formToComparable = (f) => JSON.stringify(f);

  const isDirty = useMemo(() => {
    if (!editMode || !savedForm) return false;
    return formToComparable(form) !== formToComparable(savedForm);
  }, [form, savedForm, editMode]);

  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);

  // Browser beforeunload guard
  useEffect(() => {
    if (!isDirty) return;
    const handler = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty]);

  const fetchLocations = useCallback(async () => {
    if (!projectId) return;
    try {
      const res = await axios.get(`${API}/electrical-systems/${projectId}/locations`);
      setLocations(res.data);
    } catch { /* silent */ }
  }, [projectId]);

  const fetchData = useCallback(async () => {
    if (!projectId) return;
    try {
      const res = await axios.get(`${API}/electrical-systems/${projectId}/general-info`);
      const d = res.data;
      if (d && Object.keys(d).length > 0) {
        const loaded = {
          utilityProvider: d.utilityProvider || '', serviceVoltage: d.serviceVoltage || '',
          phase: d.phase || '', serviceCapacity: d.serviceCapacity || '',
          mainSwitchboard: d.mainSwitchboard || '', serviceDisconnectRating: d.serviceDisconnectRating || '',
          estimatedLoad: d.estimatedLoad != null ? String(d.estimatedLoad) : '',
        };
        setForm(loaded);
        setSavedForm(loaded);
        setFloorPlan(d.floorPlan || null);
      }
    } catch {
      toast.error('Failed to load general information');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => { fetchData(); fetchLocations(); }, [fetchData, fetchLocations]);

  const handleChange = (key, value) => {
    setForm(prev => ({ ...prev, [key]: value }));
    if (errors[key]) setErrors(prev => ({ ...prev, [key]: null }));
  };

  const validate = () => {
    const errs = {};
    ['utilityProvider', 'serviceVoltage', 'phase', 'serviceCapacity', 'mainSwitchboard', 'serviceDisconnectRating'].forEach(k => {
      if (!form[k]?.trim()) errs[k] = 'This field is required';
    });
    if (form.estimatedLoad && (isNaN(Number(form.estimatedLoad)) || Number(form.estimatedLoad) < 0)) {
      errs.estimatedLoad = 'Must be a positive number';
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      const payload = { ...form, estimatedLoad: form.estimatedLoad ? Number(form.estimatedLoad) : null };
      await axios.put(`${API}/electrical-systems/${projectId}/general-info`, payload);
      toast.success('General information saved');
      setSavedForm({ ...form });
      setEditMode(false);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleCancelEdit = () => {
    if (savedForm) setForm({ ...savedForm });
    setErrors({});
    setEditMode(false);
  };

  /* ── Controlled input helpers for Service Voltage & Capacity ── */
  const sanitizeVoltage = (raw) => { let s = raw.replace(/[^0-9/VvYy]/g, '').replace(/[vy]/g, c => c.toUpperCase()); let seen = false; return s.replace(/V/g, m => seen ? '' : (seen = true, m)); };
  const sanitizeCapacity = (raw) => { let s = raw.replace(/[^0-9Aa]/g, '').replace(/a/g, 'A'); let seen = false; return s.replace(/A/g, m => seen ? '' : (seen = true, m)); };
  const displayVoltage = (v) => { if (!v || v === 'V') return ''; return v.endsWith('V') ? v : v + 'V'; };
  const displayCapacity = (v) => { if (!v || v === 'A') return ''; return v.endsWith('A') ? v : v + 'A'; };

  if (loading) {
    return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;
  }

  return (
    <div className="bg-white rounded-lg border border-slate-200" data-testid="general-info-section">
      {/* Body */}
      <div className="px-5 py-5 space-y-6">
        {/* Section 1: Utility Service */}
        <fieldset data-testid="section-utility-service">
          <div className="flex items-center justify-between mb-3">
            <legend className="flex items-center gap-2 text-xs font-semibold text-slate-500 uppercase tracking-wider">
              <Zap className="h-3.5 w-3.5 text-orange-500" />Utility Service
            </legend>
            {canEditCSIItems && !editMode && (
              <Button size="sm" onClick={() => setEditMode(true)} className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="general-info-edit-btn">
                <Pencil className="h-3.5 w-3.5 mr-1.5" />Edit
              </Button>
            )}
            {canEditCSIItems && editMode && (
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="sm" onClick={handleCancelEdit} data-testid="general-info-cancel-btn">Cancel</Button>
                <Button size="sm" onClick={handleSave} disabled={saving} className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="general-info-save-btn">
                  {saving ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Save className="h-3.5 w-3.5 mr-1.5" />}
                  Save Changes
                </Button>
              </div>
            )}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
            <FieldCell label="Utility Provider" required value={form.utilityProvider} editMode={editMode} error={errors.utilityProvider}>
              <Input value={form.utilityProvider} onChange={e => handleChange('utilityProvider', e.target.value)} placeholder="Enter utility provider" className={errors.utilityProvider ? 'border-red-500' : ''} data-testid="input-utilityProvider" />
            </FieldCell>
            <FieldCell label="Service Voltage" required value={displayVoltage(form.serviceVoltage)} editMode={editMode} error={errors.serviceVoltage}>
              <Input value={form.serviceVoltage} onChange={e => handleChange('serviceVoltage', sanitizeVoltage(e.target.value))} onPaste={e => { e.preventDefault(); const t = sanitizeVoltage(e.clipboardData.getData('text')); handleChange('serviceVoltage', t); }} placeholder="e.g. 480Y/277V" className={errors.serviceVoltage ? 'border-red-500' : ''} data-testid="input-serviceVoltage" />
            </FieldCell>
            <FieldCell label="Phase" required value={form.phase ? `Phase ${form.phase}` : ''} editMode={editMode} error={errors.phase}>
              <Select value={form.phase} onValueChange={v => handleChange('phase', v)}>
                <SelectTrigger className={errors.phase ? 'border-red-500' : ''} data-testid="select-phase"><SelectValue placeholder="Select phase" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">1</SelectItem>
                  <SelectItem value="3">3</SelectItem>
                </SelectContent>
              </Select>
            </FieldCell>
            <FieldCell label="Service Capacity" required value={displayCapacity(form.serviceCapacity)} editMode={editMode} error={errors.serviceCapacity}>
              <Input value={form.serviceCapacity} onChange={e => handleChange('serviceCapacity', sanitizeCapacity(e.target.value))} onPaste={e => { e.preventDefault(); const t = sanitizeCapacity(e.clipboardData.getData('text')); handleChange('serviceCapacity', t); }} placeholder="e.g. 3000A" className={errors.serviceCapacity ? 'border-red-500' : ''} data-testid="input-serviceCapacity" />
            </FieldCell>
          </div>
        </fieldset>

        <hr className="border-slate-100" />

        {/* Section 2+3: Service Equipment & Total Load (left) + Locations (right) */}
        <div className="grid grid-cols-1 sm:grid-cols-[1fr_1fr] gap-x-6 gap-y-6">
          {/* Left column: Service Equipment + Total Connected Load */}
          <div className="space-y-6">
            <fieldset data-testid="section-service-equipment">
              <legend className="flex items-center gap-2 text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">
                <Settings className="h-3.5 w-3.5 text-orange-500" />Service Equipment
              </legend>
              <div className="space-y-0">
                <FieldCell label="Main Switchboard" required value={form.mainSwitchboard} editMode={editMode} error={errors.mainSwitchboard}>
                  <Input value={form.mainSwitchboard} onChange={e => handleChange('mainSwitchboard', e.target.value)} placeholder="Enter main switchboard" className={errors.mainSwitchboard ? 'border-red-500' : ''} data-testid="input-mainSwitchboard" />
                </FieldCell>
                <FieldCell label="Service Disconnect Rating" required value={form.serviceDisconnectRating} editMode={editMode} error={errors.serviceDisconnectRating}>
                  <Input value={form.serviceDisconnectRating} onChange={e => handleChange('serviceDisconnectRating', e.target.value)} placeholder="Enter disconnect rating" className={errors.serviceDisconnectRating ? 'border-red-500' : ''} data-testid="input-serviceDisconnectRating" />
                </FieldCell>
              </div>
            </fieldset>

            <hr className="border-slate-100" />

            <fieldset data-testid="section-total-connected-load">
              <legend className="flex items-center gap-2 text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">
                <BarChart3 className="h-3.5 w-3.5 text-orange-500" />Total Connected Load
              </legend>
              <FieldCell label="Estimated Load (kW)" value={form.estimatedLoad ? `${form.estimatedLoad} kW` : ''} editMode={editMode} error={errors.estimatedLoad}>
                <Input type="number" min="0" step="any" value={form.estimatedLoad} onChange={e => handleChange('estimatedLoad', e.target.value)} onKeyDown={e => { if (e.key === '-' || e.key === 'e') e.preventDefault(); }} onPaste={e => { const v = e.clipboardData.getData('text'); if (v && Number(v) < 0) e.preventDefault(); }} placeholder="e.g. 250" className={errors.estimatedLoad ? 'border-red-500' : ''} data-testid="input-estimatedLoad" />
              </FieldCell>
            </fieldset>
          </div>

          {/* Right column: Locations block */}
          <div className="flex flex-col" data-testid="section-locations">
            <div className="flex items-center justify-between mb-3">
              <span className="flex items-center gap-2 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                <MapPin className="h-3.5 w-3.5 text-orange-500" />Locations
              </span>
              <div className="flex items-center gap-1.5">
                <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => setEditLocationsOpen(true)} data-testid="edit-locations-btn">
                  <Pencil className="h-3 w-3 mr-1" />Edit
                </Button>
                <LocationImportModal projectId={projectId} onImportComplete={fetchLocations} />
              </div>
            </div>
            <div className="flex-1 border border-slate-200 rounded-lg overflow-hidden">
              {locations.length > 0 ? (
                <div className="max-h-52 overflow-y-auto" data-testid="locations-list">
                  {locations.map((loc, i) => (
                    <div key={loc.id} className={`flex items-center gap-2 px-3 py-2 text-sm ${i > 0 ? 'border-t border-slate-100' : ''}`}>
                      <MapPin className="h-3.5 w-3.5 text-slate-300 shrink-0" />
                      <span className="text-slate-700">{loc.name}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex items-center justify-center h-24 text-sm text-slate-400 italic" data-testid="locations-empty">
                  No locations added
                </div>
              )}
            </div>
            <p className="text-[10px] text-slate-400 mt-1.5">{locations.length} location{locations.length !== 1 ? 's' : ''} across Panels, Equipment &amp; Circuits</p>
          </div>
        </div>

        <hr className="border-slate-100" />

        <fieldset data-testid="section-floor-plan">
          <legend className="flex items-center gap-2 text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">
            <FileUp className="h-3.5 w-3.5 text-orange-500" />Floor Plan
          </legend>
          <FloorPlanManager
            apiBase="/electrical-systems"
            projectId={projectId}
            floorPlan={floorPlan}
            onChange={setFloorPlan}
            editMode={editMode}
          />
        </fieldset>
      </div>

      <EditLocationsModal
        open={editLocationsOpen}
        onOpenChange={setEditLocationsOpen}
        projectId={projectId}
        onLocationsChanged={fetchLocations}
      />
    </div>
  );
};
